import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Fade,
    Grid,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import axios from "axios";
import { updateUser } from "../../redux/userRelated/userHandle";
import { patchCurrentUser, underControl } from "../../redux/userRelated/userSlice";
import { getApiBaseUrl } from "../../config/api";
import { parseFeaturePreferences } from "../../context/featurePreferences";
import Popup from "../../components/Popup";
import { BOARDING_MODES, boardingModeLabel, normalizeBoardingMode } from "../../utils/boardingUtils";
import { PASSWORD_MIN_HELPER, validatePasswordIfProvided } from "../../utils/passwordValidation";

function initialsFromName(name) {
    if (!name || typeof name !== "string") return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AdminProfile = () => {
    const dispatch = useDispatch();
    const { currentUser, status, response, loading, error } = useSelector((state) => state.user);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [schoolName, setSchoolName] = useState("");
    const [schoolCode, setSchoolCode] = useState("");
    const [manebCentreNumber, setManebCentreNumber] = useState("");
    const [schoolDistrict, setSchoolDistrict] = useState("");
    const [schoolRegion, setSchoolRegion] = useState("");
    const [boardingMode, setBoardingMode] = useState("DayOnly");
    const [password, setPassword] = useState("");

    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [saveSuccess, setSaveSuccess] = useState(false);

    const savePendingRef = useRef(false);

    const adminId = currentUser?._id || currentUser?.id;

    useEffect(() => {
        if (!adminId) return;

        let cancelled = false;

        axios
            .get(`${getApiBaseUrl()}/Admin/${adminId}`)
            .then(({ data }) => {
                if (cancelled) return;
                const hasEntity = Boolean(data?.id || data?._id);
                if (!hasEntity || data?.message) return;

                dispatch(
                    patchCurrentUser({
                        name: data.name,
                        email: data.email,
                        schoolName: data.schoolName,
                        schoolCode: data.schoolCode,
                        manebCentreNumber: data.manebCentreNumber,
                        schoolDistrict: data.schoolDistrict,
                        schoolRegion: data.schoolRegion,
                        boardingMode: data.boardingMode,
                        hasBoarding: data.hasBoarding,
                    }),
                );
            })
            .catch(() => {
                /* keep session copy */
            });

        return () => {
            cancelled = true;
        };
    }, [adminId, dispatch]);

    // Load form once per admin account — do not reset when subscription/session refreshes currentUser.
    useEffect(() => {
        if (!currentUser || !adminId) return;
        setName(currentUser.name || "");
        setEmail(currentUser.email || "");
        setSchoolName(currentUser.schoolName || "");
        setSchoolCode(currentUser.schoolCode || "");
        setManebCentreNumber(currentUser.manebCentreNumber || "");
        setSchoolDistrict(currentUser.schoolDistrict || "");
        setSchoolRegion(currentUser.schoolRegion || "");
        setBoardingMode(normalizeBoardingMode(currentUser.boardingMode, currentUser.hasBoarding));
        setPassword("");
    }, [adminId]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: avoid clobbering unsaved edits

    useEffect(() => {
        if (!savePendingRef.current) return;
        if (status === "success") {
            savePendingRef.current = false;
            setSaveSuccess(true);
            setPassword("");
            setBoardingMode(normalizeBoardingMode(currentUser?.boardingMode, currentUser?.hasBoarding));
            dispatch(underControl());
        }
        if (status === "failed") {
            savePendingRef.current = false;
            setPopupMessage(response || "Could not save profile");
            setShowPopup(true);
            dispatch(underControl());
        }
    }, [status, response, dispatch, currentUser?.boardingMode, currentUser?.hasBoarding]);

    useEffect(() => {
        if (!savePendingRef.current || loading) return;
        if (error) {
            savePendingRef.current = false;
            setPopupMessage(error?.message || "Could not save profile");
            setShowPopup(true);
            dispatch(underControl());
        }
    }, [error, loading, dispatch]);

    const displayName = currentUser?.name?.trim() || "Administrator";
    const initials = useMemo(() => initialsFromName(displayName), [displayName]);

    const submitHandler = (event) => {
        event.preventDefault();
        if (!currentUser?._id) return;
        const passwordErr = validatePasswordIfProvided(password);
        if (passwordErr) {
            setPopupMessage(passwordErr);
            setShowPopup(true);
            return;
        }
        savePendingRef.current = true;
        setSaveSuccess(false);
        const normalizedBoarding = normalizeBoardingMode(boardingMode);
        const featurePreferences = parseFeaturePreferences(currentUser.featurePreferences);
        const profileFields = {
            name: name.trim(),
            email: email.trim(),
            schoolName: schoolName.trim(),
            schoolCode: schoolCode.trim(),
            manebCentreNumber: manebCentreNumber.trim(),
            schoolDistrict: schoolDistrict.trim(),
            schoolRegion: schoolRegion.trim(),
            boardingMode: normalizedBoarding,
            hasBoarding: normalizedBoarding !== "DayOnly",
            featurePreferences,
        };
        const fields =
            password.trim() === ""
                ? profileFields
                : {
                      ...profileFields,
                      password: password.trim(),
                  };
        dispatch(updateUser(fields, currentUser._id, "Admin"));
    };

    if (!currentUser) {
        return (
            <Container sx={{ py: 6 }}>
                <Alert severity="info">Sign in as an administrator to view this profile.</Alert>
            </Container>
        );
    }

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                pb: 6,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${t.palette.grey[50]} 26%)`
                        : alpha(t.palette.common.black, 0.2),
            }}
        >
            <Container maxWidth="md" sx={{ pt: { xs: 2, sm: 3 } }}>
                <Fade in timeout={360}>
                    <Stack spacing={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 52%, ${alpha(
                                        t.palette.primary.light,
                                        0.9
                                    )} 100%)`,
                                boxShadow: (t) => `0 14px 44px ${alpha(t.palette.primary.main, 0.26)}`,
                            }}
                        >
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ sm: "center" }}>
                                <Avatar
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        fontSize: "1.5rem",
                                        fontWeight: 800,
                                        bgcolor: alpha("#fff", 0.2),
                                        color: "common.white",
                                        border: (t) => `3px solid ${alpha("#fff", 0.45)}`,
                                    }}
                                >
                                    {initials}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                        School administrator
                                    </Typography>
                                    <Typography variant="h4" component="h1" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                                        {displayName}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                        <Chip
                                            icon={<SchoolOutlinedIcon sx={{ color: "inherit !important" }} />}
                                            label={currentUser.schoolName || "School"}
                                            sx={{
                                                bgcolor: alpha("#fff", 0.18),
                                                color: "inherit",
                                                fontWeight: 600,
                                                border: "1px solid",
                                                borderColor: alpha("#fff", 0.35),
                                            }}
                                        />
                                        <Chip
                                            label={boardingModeLabel(currentUser.boardingMode, currentUser.hasBoarding)}
                                            sx={{
                                                bgcolor: alpha("#fff", 0.18),
                                                color: "inherit",
                                                fontWeight: 600,
                                                border: "1px solid",
                                                borderColor: alpha("#fff", 0.35),
                                            }}
                                        />
                                        <Chip
                                            label={currentUser.role || "Admin"}
                                            sx={{
                                                bgcolor: alpha("#fff", 0.18),
                                                color: "inherit",
                                                fontWeight: 700,
                                                border: "1px solid",
                                                borderColor: alpha("#fff", 0.35),
                                            }}
                                        />
                                    </Stack>
                                </Box>
                                <Button
                                    component={RouterLink}
                                    to="/logout"
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<LogoutOutlinedIcon />}
                                    sx={{
                                        alignSelf: { xs: "stretch", sm: "flex-start" },
                                        borderColor: alpha("#fff", 0.65),
                                        color: "common.white",
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        "&:hover": {
                                            borderColor: "common.white",
                                            bgcolor: alpha("#fff", 0.1),
                                        },
                                    }}
                                >
                                    Sign out
                                </Button>
                            </Stack>
                        </Paper>

                        {saveSuccess && (
                            <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }} onClose={() => setSaveSuccess(false)}>
                                Profile updated — your session has been refreshed with the latest details.
                            </Alert>
                        )}

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                            }}
                        >
                            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.05) }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <AccountCircleOutlinedIcon color="primary" />
                                    <Typography variant="h6" fontWeight={700}>
                                        Account overview
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Read-only snapshot; edit in the form below.
                                </Typography>
                            </Box>
                            <Divider />
                            <Grid container spacing={0} sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                                <Grid item xs={12} sm={6} sx={{ py: 2, pr: { sm: 2 } }}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <BadgeOutlinedIcon color="action" sx={{ mt: 0.25 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                Full name
                                            </Typography>
                                            <Typography variant="body1" fontWeight={600}>
                                                {currentUser.name}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6} sx={{ py: 2 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <EmailOutlinedIcon color="action" sx={{ mt: 0.25 }} />
                                        <Box sx={{ wordBreak: "break-word" }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                Email
                                            </Typography>
                                            <Typography variant="body1" fontWeight={600}>
                                                {currentUser.email}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sx={{ py: 2, borderTop: 1, borderColor: "divider" }}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <SchoolOutlinedIcon color="action" sx={{ mt: 0.25 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                School name (unique)
                                            </Typography>
                                            <Typography variant="body1" fontWeight={600}>
                                                {currentUser.schoolName}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sx={{ py: 2, borderTop: 1, borderColor: "divider" }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Account ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                                        {currentUser._id}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider />

                            <Box component="form" onSubmit={submitHandler} sx={{ p: { xs: 2, sm: 3 } }}>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    Edit profile
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Changing email or school name checks for duplicates. Leave password blank to keep your current password.
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Full name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            autoComplete="name"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            type="email"
                                            label="Email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            autoComplete="email"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="School name"
                                            value={schoolName}
                                            onChange={(e) => setSchoolName(e.target.value)}
                                            helperText="Must stay unique across the platform."
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="MoE EMIS school code"
                                            value={schoolCode}
                                            onChange={(e) => setSchoolCode(e.target.value)}
                                            helperText="Ministry registration number for census returns"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="MANEB centre number"
                                            value={manebCentreNumber}
                                            onChange={(e) => setManebCentreNumber(e.target.value)}
                                            helperText="Examination centre code for candidate returns"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="District"
                                            value={schoolDistrict}
                                            onChange={(e) => setSchoolDistrict(e.target.value)}
                                            placeholder="e.g. Lilongwe"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Region"
                                            value={schoolRegion}
                                            onChange={(e) => setSchoolRegion(e.target.value)}
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            <MenuItem value="Northern">Northern</MenuItem>
                                            <MenuItem value="Central">Central</MenuItem>
                                            <MenuItem value="Southern">Southern</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            select
                                            label="Residence offering"
                                            value={boardingMode}
                                            onChange={(e) => setBoardingMode(e.target.value)}
                                            helperText="Changing this shows or hides the Hostels & boarding module in the sidebar."
                                        >
                                            {BOARDING_MODES.map((m) => (
                                                <MenuItem key={m.value} value={m.value}>
                                                    {m.label} — {m.hint}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="New password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            autoComplete="new-password"
                                            helperText={`Optional — leave blank to keep current. ${PASSWORD_MIN_HELPER} if changing.`}
                                            inputProps={{ minLength: 8 }}
                                        />
                                    </Grid>
                                </Grid>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
                                    <Button type="submit" variant="contained" size="large" disabled={loading} startIcon={<SaveOutlinedIcon />} sx={{ borderRadius: 2, fontWeight: 700 }}>
                                        Save changes
                                    </Button>
                                </Stack>
                            </Box>
                        </Paper>
                    </Stack>
                </Fade>
            </Container>
            <Popup message={popupMessage} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
};

export default AdminProfile;
